import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronDown, BookOpen, Send, MessageSquare, Paperclip } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { pluralizeLevel } from '@/lib/pluralize'
import { DocumentPickerPanel } from '@/components/DocumentPickerPanel'
import { BottomSheet } from '@/components/BottomSheet'
import { WebSearchModeToggle } from '@/components/WebSearchModeToggle'
import { CHAT_MESSAGE_MAX_CHARS, buildMessageTooLongError } from '@/lib/chat-limits'
import { useSettings } from '@/lib/settings'
import {
  PROCESSING_STATUSES,
  isHomeAttachableDocument,
  type UiDocumentItem,
} from '@/lib/documents-ui'

const INITIAL_SHOW = 5
const PRIORITY_CODES = ['GKRF', 'TKRF', 'SKRF', 'NKRF', 'ZKRF']
const SELECTED_DOCS_KEY = 'miniapp_selected_doc_ids'
const HOME_SELECTED_DOCS_KEY = 'miniapp_home_selected_doc_ids'
const HOME_PENDING_QUERY_KEY = 'miniapp_home_pending_query'
const HOME_INPUT_MIN_HEIGHT = 38
const HOME_INPUT_MAX_HEIGHT = 360

export default function HomePage() {
  const navigate = useNavigate()
  const { settings, setWebSearchMode } = useSettings()
  const [showAll, setShowAll] = useState(false)
  const [query, setQuery] = useState('')
  const [showDocsPicker, setShowDocsPicker] = useState(false)
  const [docs, setDocs] = useState<UiDocumentItem[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsUploading, setDocsUploading] = useState(false)
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [acceptAttr, setAcceptAttr] = useState('.pdf,.docx,.txt')
  const [maxUploadMb, setMaxUploadMb] = useState(25)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [assistantError, setAssistantError] = useState<string | null>(null)
  const [docsError, setDocsError] = useState<string | null>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const queryInputRef = useRef<HTMLTextAreaElement>(null)
  const handoffToChatRef = useRef(false)

  const { data: lawTypes, isLoading, error } = useQuery({
    queryKey: ['lawTypes'],
    queryFn: () => apiClient.getLawTypes(),
  })

  // Платные фичи (анализ документов, веб-поиск) — только премиум; иначе бейдж «Pro».
  const { data: homePlan } = useQuery({
    queryKey: ['user-plan'],
    queryFn: () => apiClient.getUserPlan(),
    staleTime: 60_000,
  })
  const isPremium = homePlan?.plan === 'premium' || Boolean(homePlan?.features?.doc_analysis)
  // Free: ограниченный доступ N/день. canDocs/canWeb = премиум ИЛИ остался дневной лимит.
  const canDocs = isPremium || (homePlan?.free_doc_analysis_remaining ?? 0) > 0
  const canWeb = isPremium || (homePlan?.free_web_search_remaining ?? 0) > 0

  const hasProcessingDocs = useMemo(
    () =>
      docs.some((doc) => {
        const scope = String(doc.scope || '').toLowerCase()
        if (scope === 'chat') return false
        return PROCESSING_STATUSES.has(String(doc.status || '').toLowerCase())
      }),
    [docs],
  )

  const attachableDocs = useMemo(
    () => docs.filter((doc) => isHomeAttachableDocument(doc)),
    [docs],
  )

  const loadDocs = useCallback(async () => {
    setDocsLoading(true)
    setDocsError(null)
    try {
      const response = await apiClient.getDocuments({ page: 1, pageSize: 200 })
      const items = Array.isArray(response.items) ? response.items : []
      setDocs(items)
      const selectableIds = new Set(
        items
          .filter((item) => isHomeAttachableDocument(item))
          .map((item) => String(item.id || '').trim())
          .filter(Boolean),
      )
      setSelectedDocIds((prev) => {
        const filtered = prev.filter((id) => selectableIds.has(id))
        return filtered.length === prev.length && filtered.every((id, index) => id === prev[index])
          ? prev
          : filtered
      })
    } catch (err) {
      setDocs([])
      setDocsError(err instanceof Error ? err.message : 'Ошибка загрузки документов')
    } finally {
      setDocsLoading(false)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (handoffToChatRef.current) return
      try {
        localStorage.removeItem(HOME_SELECTED_DOCS_KEY)
      } catch {
        // ignore
      }
    }
  }, [])

  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const capabilities = await apiClient.getDocumentsCapabilities()
        const item = capabilities?.item || {}
        if (Array.isArray(item.allowed_extensions) && item.allowed_extensions.length > 0) {
          setAcceptAttr(item.allowed_extensions.join(','))
        }
        if (typeof item.max_upload_mb === 'number') {
          setMaxUploadMb(item.max_upload_mb)
        }
      } catch {
        // fallback
      }
    }
    void loadCapabilities()
  }, [])

  useEffect(() => {
    void loadDocs()
  }, [loadDocs])

  useEffect(() => {
    if (!hasProcessingDocs) return
    const timer = window.setInterval(() => {
      void loadDocs()
    }, 2500)
    return () => window.clearInterval(timer)
  }, [hasProcessingDocs, loadDocs])

  useEffect(() => {
    if (!activeJobId) return
    const timer = window.setInterval(async () => {
      try {
        const job = await apiClient.getDocumentsJob(activeJobId)
        const status = String(job?.item?.status || '').toLowerCase()
        const readyIds = (job?.item?.documents || [])
          .filter((item) => String(item?.status || '').toLowerCase() === 'ready' && String(item?.scope || '').toLowerCase() !== 'chat')
          .map((item) => String(item?.id || '').trim())
          .filter(Boolean)
        if (readyIds.length > 0) {
          setSelectedDocIds((prev) => Array.from(new Set([...prev, ...readyIds])))
        }
        await loadDocs()
        if (status === 'completed' || status === 'failed') {
          setActiveJobId(null)
        }
      } catch {
        await loadDocs()
      }
    }, 2000)
    return () => window.clearInterval(timer)
  }, [activeJobId, loadDocs])

  const sortedTypes = (() => {
    if (!lawTypes) return undefined
    const prioritySet = new Set(PRIORITY_CODES)
    const pinned = PRIORITY_CODES
      .map(code => lawTypes.find(t => t.code === code))
      .filter(Boolean) as typeof lawTypes
    const rest = lawTypes
      .filter(t => !prioritySet.has(t.code))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    return [...pinned, ...rest]
  })()

  const displayedTypes = showAll ? sortedTypes : sortedTypes?.slice(0, INITIAL_SHOW)
  const hasMore = (lawTypes?.length || 0) > INITIAL_SHOW

  const handleAskQuestion = () => {
    const text = query.trim()
    if (!text) return
    if (text.length > CHAT_MESSAGE_MAX_CHARS) {
      setAssistantError(buildMessageTooLongError(CHAT_MESSAGE_MAX_CHARS))
      return
    }
    setAssistantError(null)
    try {
      handoffToChatRef.current = true
      localStorage.setItem(HOME_SELECTED_DOCS_KEY, JSON.stringify(selectedDocIds))
      localStorage.setItem(SELECTED_DOCS_KEY, JSON.stringify(selectedDocIds))
      localStorage.setItem(HOME_PENDING_QUERY_KEY, text)
    } catch {
      // ignore
    }
    const qParam = text.length <= 1200 ? `&q=${encodeURIComponent(text)}` : ''
    navigate(
      `/chat?new=1&keep_docs=1&docs_source=home&home_handoff=1${qParam}&web_mode=${encodeURIComponent(settings.webSearchMode)}`,
    )
  }

  const resizeQueryInput = useCallback(() => {
    const el = queryInputRef.current
    if (!el) return
    el.style.height = '0px'
    const nextHeight = Math.min(Math.max(el.scrollHeight, HOME_INPUT_MIN_HEIGHT), HOME_INPUT_MAX_HEIGHT)
    el.style.height = `${nextHeight}px`
    el.style.overflowY = el.scrollHeight > HOME_INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
  }, [])

  useEffect(() => {
    resizeQueryInput()
  }, [query, resizeQueryInput])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAskQuestion()
    }
  }

  const toggleSelectedDoc = (doc: UiDocumentItem) => {
    const docId = String(doc.id || '').trim()
    if (!docId) return
    const ready = String(doc.status || '').toLowerCase() === 'ready'
    const isChatScope = String(doc.scope || '').toLowerCase() === 'chat'
    if (!ready || isChatScope) return
    setSelectedDocIds((prev) => (
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    ))
  }

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || docsUploading) return
    setDocsUploading(true)
    setDocsError(null)
    try {
      const result = await apiClient.uploadDocuments(Array.from(files))
      const jobId = String(result?.item?.job_id || '').trim()
      if (jobId) setActiveJobId(jobId)
      await loadDocs()
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Ошибка загрузки документов')
    } finally {
      setDocsUploading(false)
    }
  }

  return (
    <div className="home-page flex flex-col min-h-full">
      <div className="home-hero px-4 pt-3 pb-2.5">
        <h1 className="text-lg font-bold leading-tight">Кодексы РФ + ИИ ассистент</h1>
      </div>

      <div className="home-content flex-1 px-3 pb-3">
        <div
          className="home-assistant-block"
          style={{
            padding: '14px',
            borderRadius: 'var(--glass-radius)',
            background: 'var(--tg-theme-button-color, #3b82f6)',
            boxShadow: '0 4px 20px rgba(59,130,246,0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <MessageSquare size={15} className="text-white" />
            </div>
            <div>
              <p className="text-[13px] font-semibold leading-tight text-white">ИИ-ассистент</p>
              <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.7)' }}>Задайте вопрос по законодательству</p>
            </div>
          </div>
          <input
            ref={uploadInputRef}
            type="file"
            accept={acceptAttr}
            multiple
            className="hidden"
            onChange={(e) => void uploadFiles(e.target.files)}
            disabled={docsUploading}
          />
          <div className="flex items-end gap-2">
            <button
              onClick={() => { if (canDocs) setShowDocsPicker((prev) => !prev); else navigate('/settings') }}
              className="shrink-0 rounded-xl flex items-center justify-center transition-all active:scale-90 relative"
              style={{ width: '38px', height: '38px', background: 'rgba(255,255,255,0.2)' }}
              title={canDocs ? 'Прикрепить документы' : 'Бесплатный дневной лимит исчерпан — доступно на тарифах Лайт и Про'}
            >
              <Paperclip size={16} className="text-white" />
              {isPremium && selectedDocIds.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 rounded-full bg-white text-telegram-button text-[9px] font-bold flex items-center justify-center"
                  style={{ minWidth: '16px', height: '16px', padding: '0 4px' }}
                  title={`Прикреплено документов: ${selectedDocIds.length}`}
                >
                  {selectedDocIds.length}
                </span>
              )}
              {!canDocs && (
                <span
                  className="absolute -top-1 -right-1 rounded-full bg-[#ee3f58] text-white text-[7px] font-extrabold flex items-center justify-center"
                  style={{ minWidth: '15px', height: '13px', padding: '0 3px' }}
                >
                  PRO
                </span>
              )}
            </button>
            <WebSearchModeToggle
              mode={settings.webSearchMode}
              onChange={setWebSearchMode}
              tone="home"
              disabled={docsUploading}
              locked={!canWeb}
              onLockedClick={() => navigate('/settings')}
            />
            <textarea
              ref={queryInputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                if (assistantError) setAssistantError(null)
              }}
              onKeyDown={handleKeyDown}
              maxLength={CHAT_MESSAGE_MAX_CHARS}
              placeholder="Например: как расторгнуть договор?"
              rows={1}
              className="flex-1 text-[13px]"
              style={{
                padding: '9px 14px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.92)',
                color: '#1a1a2e',
                border: 'none',
                outline: 'none',
                lineHeight: '1.35',
                minHeight: `${HOME_INPUT_MIN_HEIGHT}px`,
                maxHeight: `${HOME_INPUT_MAX_HEIGHT}px`,
                resize: 'none',
              }}
            />
            <button
              onClick={handleAskQuestion}
              disabled={!query.trim()}
              className="shrink-0 rounded-xl flex items-center justify-center transition-all active:scale-90 disabled:opacity-40"
              style={{ width: '38px', height: '38px', background: 'rgba(255,255,255,0.2)' }}
            >
              <Send size={16} className="text-white" />
            </button>
          </div>
          {assistantError && (
            <p className="mt-2 text-[10px]" style={{ color: 'rgba(255,255,255,0.9)' }}>
              {assistantError}
            </p>
          )}
        </div>

        <BottomSheet
          open={showDocsPicker}
          onClose={() => setShowDocsPicker(false)}
          title="Документы"
          description="Выберите документы, которые нужно учитывать в новом чате."
          maxBodyHeight="72vh"
        >
          <DocumentPickerPanel
            inSheet
            selectedDocIds={selectedDocIds}
            documents={attachableDocs}
            docsLoading={docsLoading}
            docsUploading={docsUploading}
            docsError={docsError}
            processingInfo={activeJobId ? 'Загрузка принята, документ индексируется…' : null}
            acceptAttr={acceptAttr}
            maxUploadMb={maxUploadMb}
            uploadButtonTitle={`Загрузить глобальный документ (${acceptAttr}, до ${maxUploadMb} MB)`}
            emptyText="Нет доступных документов для прикрепления в новый чат."
            onUploadClick={() => uploadInputRef.current?.click()}
            onRefresh={() => {
              void loadDocs()
            }}
            onToggleDocument={toggleSelectedDoc}
            onClose={() => setShowDocsPicker(false)}
          />
        </BottomSheet>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-telegram-button/30 border-t-telegram-button" />
              <span className="text-xs text-telegram-hint">Загрузка кодексов...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm font-medium mb-1">Не удалось загрузить</p>
            <p className="text-xs text-telegram-hint">Попробуйте позже</p>
          </div>
        )}

        {!isLoading && !error && lawTypes && lawTypes.length > 0 && (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-telegram-hint mt-3 mb-1.5" style={{ paddingLeft: '2px' }}>Кодексы</p>
            <div className="home-codex-list" style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {displayedTypes?.map((lawType, index) => (
                <a
                  key={lawType.code}
                  href={`/law/${lawType.code}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/law/${lawType.code}`) }}
                  className="home-codex-card flex items-center gap-2.5 active:opacity-70 transition-opacity glass-appear"
                  style={{
                    padding: '11px 12px',
                    borderRadius: '10px',
                    borderLeft: '3px solid var(--tg-theme-button-color, #3b82f6)',
                    background: 'var(--glass-bg-card)',
                    animationDelay: `${index * 40}ms`,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium leading-snug">{lawType.name}</div>
                    {lawType.children_count != null && lawType.children_count > 0 && (
                      <div className="text-[10px] text-telegram-hint/70 mt-0.5">
                        {pluralizeLevel(lawType.children_count, lawType.hierarchy?.[0]?.level || 'section')}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="text-telegram-hint/40 shrink-0" size={15} />
                </a>
              ))}
            </div>

            {hasMore && !showAll && (
              <button
                onClick={() => setShowAll(true)}
                className="home-show-all w-full flex items-center justify-center gap-1.5 py-2 mt-1 text-telegram-link text-[12px] font-medium active:opacity-60 transition-opacity"
              >
                <span>Ещё ({(sortedTypes?.length || 0) - INITIAL_SHOW})</span>
                <ChevronDown size={14} />
              </button>
            )}
            {showAll && (
              <button
                onClick={() => setShowAll(false)}
                className="home-show-all w-full flex items-center justify-center gap-1.5 py-2 mt-1 text-telegram-hint text-[11px] active:opacity-60 transition-opacity"
              >
                Свернуть
              </button>
            )}
          </>
        )}

        {!isLoading && !error && (!lawTypes || lawTypes.length === 0) && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen size={24} className="text-telegram-hint/50 mb-2" />
            <p className="text-sm font-medium mb-1">Кодексы не загружены</p>
            <p className="text-xs text-telegram-hint">Данные пока недоступны</p>
          </div>
        )}
      </div>
    </div>
  )
}
